import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../Home/Home.css';
import './SubscriptionPage.css';
import {
  formatVnd,
  getOrderedPackageEntries,
  summarizeIncludedFeatures,
} from '../../utils/packageCatalog';

function PackageCard({ pkg, isAuthenticated }) {
  const includedSummary = summarizeIncludedFeatures(pkg);
  const primaryLink = pkg?.isPoa ? '/contact' : (isAuthenticated ? '/control-panel' : '/signup');
  const primaryLabel = pkg?.isPoa
    ? 'Talk to Sales'
    : (isAuthenticated ? 'Open Control Panel' : (pkg?.key === 'free' ? 'Start Free' : 'Get Started'));

  return (
    <div className={`h-card sp-card${pkg?.isPoa ? ' sp-card--enterprise' : ''}`}>
      <div className="sp-card__top">
        <div>
          <div className="h-eyebrow" style={{ marginBottom: 8 }}>{pkg?.label || 'Package'}</div>
          <div className="sp-card__price">
            {pkg?.isPoa ? 'POA' : `${formatVnd(pkg?.basePrice)} VND`}
          </div>
          {!pkg?.isPoa && <div className="sp-card__cycle">per site / month</div>}
        </div>
        <div className="sp-card__badges">
          <span className={`sp-badge ${pkg?.advertisingEnabled ? 'sp-badge--ads' : 'sp-badge--clean'}`}>
            {pkg?.advertisingEnabled ? 'Ads On' : 'Ads Off'}
          </span>
          {pkg?.maxSitesPerAccount === 1 && (
            <span className="sp-badge sp-badge--limit">Single-site</span>
          )}
        </div>
      </div>

      <p className="sp-card__summary">{pkg?.summary || ''}</p>

      <div className="sp-card__section">
        <div className="sp-card__label">Included Per Site</div>
        <div className="sp-card__value">{includedSummary || 'No included items configured'}</div>
      </div>

      <div className="sp-card__section">
        <div className="sp-card__label">Website</div>
        <div className="sp-card__value">{pkg?.websitePolicy || '—'}</div>
      </div>

      <div className="sp-card__section">
        <div className="sp-card__label">AI</div>
        <div className="sp-card__value">{pkg?.aiPolicy || '—'}</div>
      </div>

      <div className="sp-card__section">
        <div className="sp-card__label">API</div>
        <div className="sp-card__value">{pkg?.apiPolicy || '—'}</div>
      </div>

      <div className="sp-card__actions">
        <Link to={primaryLink} className="h-btn h-btn--primary">{primaryLabel}</Link>
        <Link to="/contact" className="h-btn h-btn--ghost">Book a Demo</Link>
      </div>
    </div>
  );
}

function AddOnRow({ label, value }) {
  return (
    <div className="sp-addon-row">
      <span>{label}</span>
      <strong>{formatVnd(value)} VND</strong>
    </div>
  );
}

export default function SubscriptionPage() {
  const [packageCatalog, setPackageCatalog] = useState({});
  const [billingDefaults, setBillingDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isAuthenticated = Boolean(localStorage.getItem('token'));

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/api/entitlements/public-packages');
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.message || `Failed to load packages (${res.status})`);
        }
        if (!ignore) {
          setPackageCatalog(json?.packageCatalog && typeof json.packageCatalog === 'object' && !Array.isArray(json.packageCatalog)
            ? json.packageCatalog
            : {});
          setBillingDefaults(json?.billingDefaults && typeof json.billingDefaults === 'object' && !Array.isArray(json.billingDefaults)
            ? json.billingDefaults
            : {});
        }
      } catch (loadError) {
        if (!ignore) setError(loadError.message || 'Failed to load package catalog.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, []);

  const packageCards = useMemo(() => getOrderedPackageEntries(packageCatalog), [packageCatalog]);

  return (
    <div className="h-home">
      <section className="h-section sp-hero">
        <div className="h-container">
          <p className="h-eyebrow">Pricing</p>
          <h1 className="sp-title">Simple site-based packages, one account-level invoice.</h1>
          <p className="h-section__body sp-hero__body">
            Choose a package per site, then roll everything up into one customer account. Free and Basic keep affiliate inventory on. Standard and above remove ads and unlock a cleaner branded experience.
          </p>
          <div className="h-ctas">
            <Link to={isAuthenticated ? '/control-panel' : '/signup'} className="h-btn h-btn--primary">
              {isAuthenticated ? 'Open Control Panel' : 'Start Free'}
            </Link>
            <Link to="/contact" className="h-btn h-btn--ghost">Book a Demo</Link>
          </div>
        </div>
      </section>

      <section className="h-section h-section--alt">
        <div className="h-container">
          <p className="h-eyebrow">Commercial Packages</p>
          <h2 className="h-section__title">Packages shown in VND and kept in sync with admin pricing.</h2>
          <p className="h-section__body">
            Pricing is monthly and site-based. Your runtime account tier currently follows the highest package used on the account, which keeps the commercial and live entitlement story aligned while the platform is still operator-managed.
          </p>

          {loading && (
            <div className="sp-state">Loading packages…</div>
          )}

          {!loading && error && (
            <div className="sp-state sp-state--error">
              {error}
            </div>
          )}

          {!loading && !error && (
            <div className="sp-grid">
              {packageCards.map((pkg) => (
                <PackageCard
                  key={pkg.key}
                  pkg={pkg}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="h-section">
        <div className="h-container">
          <p className="h-eyebrow">Global Add-Ons</p>
          <h2 className="h-section__title">Extra features stay predictable.</h2>
          <p className="h-section__body">
            Once a site moves beyond the included allowances, add-ons are priced from the same global defaults used in admin billing.
          </p>
          <div className="sp-addon-panel">
            <AddOnRow label="Extra TV screen" value={billingDefaults?.screens || 0} />
            <AddOnRow label="Extra music player" value={billingDefaults?.musicPlayers || 0} />
            <AddOnRow label="Extra website" value={billingDefaults?.websites || 0} />
            <AddOnRow label="Extra captive Wi-Fi setup" value={billingDefaults?.wifi || 0} />
            <AddOnRow label="Extra menu QR" value={billingDefaults?.menuQrs || 0} />
            <AddOnRow label="Extra product QR" value={billingDefaults?.productQrs || 0} />
            <AddOnRow label="Extra service QR" value={billingDefaults?.serviceQrs || 0} />
            <AddOnRow label="Extra info QR" value={billingDefaults?.infoQrs || 0} />
          </div>
        </div>
      </section>

      <section className="h-cta-band">
        <div className="h-container">
          <h2>Need a mixed multi-site setup or an enterprise group deal?</h2>
          <p>
            Start with the closest package per site, then we can shape the commercial agreement around your rollout.
          </p>
          <div className="h-ctas" style={{ justifyContent: 'center' }}>
            <Link to="/contact" className="h-btn h-btn--primary">Talk to Sales</Link>
            <Link to={isAuthenticated ? '/control-panel' : '/signup'} className="h-btn h-btn--ghost">
              {isAuthenticated ? 'Open Control Panel' : 'Start Free'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
